import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { NetworkInfo } from '../../models/NetworkInfo';
import { Graf } from '../../models/nets/GraphExtension';
import { Line } from '../../models/shapes/Line';
import { getDisplayedShapes as getDisplayedShapesUtil } from '../utils/shapeFilters';

/**
 * Manages network and graph-related state
 */
export class NetworkState extends BaseState {
  // RxJS subjects for reactive state management
  public graphs$ = new BehaviorSubject<Graf[]>([]);
  public updateCalculations$ = new Subject<any>();

  private state = {
    // Network info
    preNetInfo: new NetworkInfo(null, null, null, null, null, null, null),
    
    // Graphs
    graphs: [] as Graf[],
    
    // Sigma instance for visualization
    sigmaInstance: null as any,
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.emitStateChange(this.state);
  }

  // Network info
  getPreNetInfo(): NetworkInfo {
    return this.state.preNetInfo;
  }

  setPreNetInfo(netInfo: NetworkInfo) {
    this.state.preNetInfo = netInfo;
    this.emitStateChange({ preNetInfo: netInfo });
  }

  // Graphs management
  getGraphs(): Graf[] {
    return [...this.state.graphs];
  }

  setGraphs(graphs: Graf[]) {
    this.state.graphs = [...graphs];
    this.graphs$.next(this.state.graphs);
  }

  addGraph(graph: Graf) {
    this.state.graphs.push(graph);
    this.graphs$.next(this.state.graphs);
  }

  removeGraph(graph: Graf) {
    const index = this.state.graphs.indexOf(graph);
    if (index > -1) {
      this.state.graphs.splice(index, 1);
      this.graphs$.next(this.state.graphs);
    }
  }

  // Sigma instance
  getSigmaInstance(): any {
    return this.state.sigmaInstance;
  }

  setSigmaInstance(instance: any) {
    this.state.sigmaInstance = instance;
    this.emitStateChange({ sigmaInstance: instance });
  }

  // Network operations
  addNodesToNetwork(currentSegment: Line, shapes: any[]) {
    // This is a complex operation that should be moved to a dedicated service
    // For now, we'll keep the original logic but emit events
    this.updateCalculations$.next({ type: 'addNodes', segment: currentSegment, shapes });
  }

  removeNodesFromNetwork(selectedSegments: Line[]) {
    // This is a complex operation that should be moved to a dedicated service
    // For now, we'll keep the original logic but emit events
    this.updateCalculations$.next({ type: 'removeNodes', segments: selectedSegments });
  }

  copyNodesFromNetwork(selectedSegments: Line[]) {
    // This is a complex operation that should be moved to a dedicated service
    // For now, we'll keep the original logic but emit events
    this.updateCalculations$.next({ type: 'copyNodes', segments: selectedSegments });
  }

  updateGraphNodesCoordinatesZoom(graph: any, shapes: any[]) {
    // This is a complex operation that should be moved to a dedicated service
    // For now, we'll keep the original logic but emit events
    this.updateCalculations$.next({ type: 'updateCoordinates', graph, shapes });
  }

  updateGraphNodesCoordinatesPan() {
    // This is a complex operation that should be moved to a dedicated service
    // For now, we'll keep the original logic but emit events
    this.updateCalculations$.next({ type: 'updateCoordinatesPan' });
  }
}
